import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useQuery } from '@tanstack/react-query';
import { GoodsReceiptSchema, PurchaseOrderSchema } from '@taqon/contracts';
import { getPage } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, Section } from '../../shared/ui/page';
import { RecordLink } from '../finance/detail';
import { GR_API, GR_TYPE, OriginChip, PO_API, PO_TYPE } from './purchasing';

/**
 * A supplier's purchase orders and receipts (FRD-PRC-041, PRC-063: traceable both ways), each
 * shown only where the session may read that record type.
 */
export function SupplierPurchasing({ supplierId }: { supplierId: string }) {
  const session = useSession();
  const canSeeOrders = hasCapability(session.data, `${PO_TYPE}:view`);
  const canSeeReceipts = hasCapability(session.data, `${GR_TYPE}:view`);
  const orders = useQuery({
    queryKey: ['procurement', 'purchase-orders', 'of-supplier', supplierId],
    enabled: canSeeOrders,
    queryFn: () =>
      getPage(
        `${PO_API}?pageSize=50&sort=-document_no&filter[supplier_id]=${supplierId}`,
        PurchaseOrderSchema,
      ),
  });
  const receipts = useQuery({
    queryKey: ['procurement', 'goods-receipts', 'of-supplier', supplierId],
    enabled: canSeeReceipts,
    queryFn: () =>
      getPage(
        `${GR_API}?pageSize=50&sort=-document_no&filter[supplier_id]=${supplierId}`,
        GoodsReceiptSchema,
      ),
  });
  return (
    <>
      {canSeeOrders && (
        <Section title="Purchase orders">
          {orders.isPending && <LoadingState label="Loading purchase orders" />}
          {orders.isError && <ErrorState message={orders.error.message} />}
          {orders.data?.items.length === 0 && <EmptyState title="No purchase orders" />}
          {orders.data !== undefined && orders.data.items.length > 0 && (
            <Table size="small" aria-label="Purchase orders with this supplier">
              <TableHead>
                <TableRow>
                  <TableCell>Order</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Origin</TableCell>
                  <TableCell align="right">Total incl. VAT</TableCell>
                  <TableCell align="right">Outstanding</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.data.items.map((o) => (
                  <TableRow key={o.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <RecordLink to={`/procurement/purchase-orders/${o.id}`}>
                        {o.documentNo}
                      </RecordLink>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{o.poDate}</TableCell>
                    <TableCell>
                      <OriginChip origin={o.origin} />
                    </TableCell>
                    <TableCell align="right">{o.grossTotal}</TableCell>
                    <TableCell align="right">{o.outstandingValue}</TableCell>
                    <TableCell>
                      <StatusChip status={o.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Section>
      )}
      {canSeeReceipts && (
        <Section title="Receipts">
          {receipts.isPending && <LoadingState label="Loading receipts" />}
          {receipts.isError && <ErrorState message={receipts.error.message} />}
          {receipts.data?.items.length === 0 && <EmptyState title="No receipts" />}
          {receipts.data !== undefined && receipts.data.items.length > 0 && (
            <Table size="small" aria-label="Receipts from this supplier">
              <TableHead>
                <TableRow>
                  <TableCell>Receipt</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Purchase order</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {receipts.data.items.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <RecordLink to={`/procurement/receipts/${r.id}`}>{r.documentNo}</RecordLink>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.receiptDate}</TableCell>
                    <TableCell>{r.purchaseOrderNo}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Section>
      )}
    </>
  );
}
